import React from 'react';
import { Box, Typography } from '@mui/material';
import { parseTokens } from '@/shared/lib/sessionTokens/parseTokens';
import { usePlannerOperatorMaps } from '../context/PlannerOperatorContext';
import { PlannerChip } from './PlannerChip';
import { OperatorChip } from './OperatorChip';
import { ClientChip } from './ClientChip';
import { TimeslotSpan } from './TimeslotSpan';
import { TimeslotDiff } from './TimeslotDiff';
import { AddressSpan } from './AddressSpan';
import { fontSize, typography } from '@/shared/theme';
import { isKnownClientId } from '@/shared/lib/sessionTokens/clientIds';

interface TokenizedValueProps {
  value: string;
}

export function TokenizedValue({ value }: TokenizedValueProps) {
  const maps = usePlannerOperatorMaps();
  const parts = parseTokens(value);

  if (isKnownClientId(value)) {
    return <ClientChip clientId={value} />;
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        whiteSpace: 'pre-line',
        maxWidth: '100%',
      }}
    >
      {parts.map((part, index) => {
        if (part.kind === 'text') {
          return (
            <Typography
              key={index}
              component="span"
              sx={{
                fontSize: fontSize.sm,
                fontWeight: typography.cell.fontWeight,
                color: 'text.primary',
                whiteSpace: 'pre-line',
              }}
            >
              {part.value}
            </Typography>
          );
        }
        if (part.kind === 'planner') {
          const planner = maps?.plannerMap.get(part.payload);
          return <PlannerChip key={index} plannerId={part.payload} planner={planner} />;
        }
        if (part.kind === 'manager') {
          const operator = maps?.operatorMap.get(part.payload);
          return <OperatorChip key={index} operatorId={part.payload} operator={operator} />;
        }
        if (part.kind === 'timeslot') {
          return <TimeslotSpan key={index} raw={part.payload} />;
        }
        if (part.kind === 'timeslot-diff') {
          return <TimeslotDiff key={index} from={part.from} to={part.to} />;
        }
        if (part.kind === 'address') {
          return <AddressSpan key={index} value={part.payload} />;
        }
        if (part.kind === 'client') {
          return <ClientChip key={index} clientId={part.payload} />;
        }
        return null;
      })}
    </Box>
  );
}
